import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Breadcrumbs from "@mui/joy/Breadcrumbs";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import { Alert, CircularProgress } from "@mui/joy";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DoneIcon from "@mui/icons-material/Done";
import CancelIcon from "@mui/icons-material/Cancel";
import Sidebar from "../../../components/Sidebar";
import Header from "../../../components/Header";
import { UploadCsv } from "../../../api/methods";

export default function UploadCSV() {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = React.useState<boolean | null>(
    null
  );
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("hi");

    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadSuccess(null);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (selectedFile) {
      setUploading(true);
      try {
        const response = await UploadCsv(selectedFile);
        console.log("File uploaded successfully:", response.data);
        setUploadSuccess(true);
        setSelectedFile(null);
        setTimeout(() => {
          setUploadSuccess(null);
        }, 5000);
      } catch (error) {
        console.error("Upload failed:", error);
        setUploadError("Failed to upload file. Please try again.");
        setUploadSuccess(false);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploading(false);
    clearFileInput();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      const event = new Event("change", { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
    }
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: {
            xs: "calc(12px + var(--Header-height))",
            sm: "calc(12px + var(--Header-height))",
            md: 3,
          },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100dvh",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Breadcrumbs
            size="sm"
            aria-label="breadcrumbs"
            separator={<ChevronRightRoundedIcon fontSize="small" />}
            sx={{ pl: 0 }}
          >
            <Link
              underline="none"
              color="neutral"
              href="#some-link"
              aria-label="Home"
            >
              <HomeRoundedIcon />
            </Link>
            <Typography color="primary" fontWeight={500} fontSize={12}>
              Fichier CSV
            </Typography>
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            display: "flex",
            mb: 1,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "start", sm: "center" },
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Typography level="h2" component="h1">
            Télécharger votre CSV
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
            mt: 4,
            border: "2px dashed",
            borderColor: "gray",
            borderRadius: "md",
            p: 4,
          }}
        >
          <Typography level="body-md" sx={{ mb: 2 }}>
            Faites glisser et déposez votre fichier CSV ici, ou cliquez pour
            sélectionner un fichier.
          </Typography>
          <Button
            component="label"
            startDecorator={<UploadFileIcon />}
            variant="outlined"
            color="primary"
            sx={{ mb: 2 }}
            disabled={selectedFile != null}
          >
            {selectedFile
              ? `Fichier sélectionné : ${selectedFile.name}`
              : "Sélectionner un fichier CSV"}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => handleFileChange(e)}
            />
          </Button>
          {uploadSuccess !== null && (
            <Alert
              startDecorator={<DoneIcon />}
              color={uploadSuccess ? "success" : "danger"}
              variant="soft"
              sx={{ mt: 2 }}
            >
              {uploadSuccess
                ? "Téléchargement réussi!"
                : "Échec du téléchargement."}
            </Alert>
          )}
          {selectedFile && (
            <>
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                {uploading ? (
                  <CircularProgress size="sm" />
                ) : (
                  <>
                  <Button
                    onClick={handleUpload}
                    color="primary"
                    startDecorator={<CloudUploadIcon />}
                    disabled={!selectedFile || uploading}
                    sx={{ position: "relative" }}
                  >
                    Télécharger
                  </Button>
                
                <Button
                  onClick={handleCancel}
                  color="danger"
                  startDecorator={<CancelIcon />}
                >
                  Annuler
                </Button>
                </>)}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
